import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar, AlertCircle, Trash2, RefreshCw } from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { StatusBadge } from '../components/common/StatusBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { AIEvaluation } from '../components/evaluation/AIEvaluation';
import { EvaluationsSection } from '../components/evaluation/EvaluationsSection';
import { AttachmentCard } from '../components/AttachmentCard';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReEvaluateDialog, setShowReEvaluateDialog] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id!),
    enabled: !!id,
    // Poll every 2s while evaluation is pending (handles DynamoDB eventual consistency)
    refetchInterval: (query) =>
      query.state.data?.project.status === 'PENDING_EVALUATION' ? 2000 : false,
  });

  const { data: framework } = useQuery({
    queryKey: ['framework'],
    queryFn: api.getPublicFramework,
  });

  const submitMutation = useMutation({
    mutationFn: () => api.submitForReview(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      setShowConfirmDialog(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteProject(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      showToast('Project deleted successfully', 'success');
      navigate('/dashboard');
    },
    onError: () => {
      showToast('Failed to delete project', 'error');
    },
  });

  const reEvaluateMutation = useMutation({
    mutationFn: () => api.adminReEvaluate(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      showToast('AI evaluation triggered. Page will refresh when complete.', 'success');
      setShowReEvaluateDialog(false);
    },
    onError: () => {
      showToast('Failed to trigger AI evaluation', 'error');
    },
  });

  const uploadPdfMutation = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      return api.uploadPdf(id!, { pdf_base64: base64, filename: file.name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      showToast('PDF uploaded successfully', 'success');
    },
    onError: () => {
      showToast('Failed to upload PDF', 'error');
    },
  });

  const removePdfMutation = useMutation({
    mutationFn: () => api.removePdf(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      showToast('PDF removed', 'success');
    },
    onError: () => {
      showToast('Failed to remove PDF', 'error');
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: async () => {
      const { download_url, filename } = await api.getPdfDownloadUrl(id!);
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      link.click();
    },
    onError: () => {
      showToast('Failed to download PDF', 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="p-8 text-center">
            <h2 className="text-xl font-semibold text-wolf-100 mb-2">Project Not Found</h2>
            <p className="text-wolf-300 mb-6">
              The project you're looking for doesn't exist or couldn't be loaded.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-action hover:text-action-light transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const { project, evaluations } = data;
  const isSubmitter = user?.email === project.submitter_id;
  const canSubmitForReview = isSubmitter && project.status === 'DRAFT';
  const aiEvaluation = evaluations?.find((e) => e.is_ai);
  const humanEvaluation = evaluations?.find((e) => !e.is_ai && e.evaluator_id === user?.email);
  const otherReviewerEvaluations = evaluations?.filter(
    (e) => !e.is_ai && e.evaluator_id !== user?.email
  ) ?? [];
  const isAdmin = user?.role === 'admin';
  const isReviewer = user?.role === 'reviewer' || isAdmin;
  const canDelete = isAdmin || project?.submitter_id === user?.email;
  const canReEvaluate = isAdmin && project.status !== 'DRAFT';

  // For submitters: show "Under Review" for any status after DRAFT
  const displayStatus = isSubmitter && project.status !== 'DRAFT' && project.status !== 'CONSENSUS_REACHED'
    ? 'UNDER_REVIEW'
    : project.status;

  const formattedDate = new Date(project.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-wolf-300 hover:text-wolf-100 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          <h1 className="text-3xl font-bold text-wolf-100 flex-1">{project.title}</h1>
          <div className="flex items-center gap-3">
            <StatusBadge status={displayStatus} />
            {canReEvaluate && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowReEvaluateDialog(true)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {aiEvaluation ? 'Re-run AI Evaluation' : 'Run AI Evaluation'}
              </Button>
            )}
            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-wolf-500 text-sm">
          <Calendar className="w-4 h-4" />
          <span>
            Submitted by <span className="text-wolf-300">{project.submitter_name}</span> on {formattedDate}
          </span>
        </div>
      </div>

      {/* Submit for Review Button */}
      {canSubmitForReview && (
        <div className="mb-6">
          <Button onClick={() => setShowConfirmDialog(true)}>
            Submit for Review
          </Button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-wolf-950/80">
          <Card className="max-w-md mx-4 !bg-wolf-900">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-wolf-100 mb-4">Confirm Submission</h3>
              <p className="text-wolf-300 mb-6">
                Once submitted, you cannot edit this project. Are you sure you want to submit it for review?
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmDialog(false)}
                  disabled={submitMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => submitMutation.mutate()}
                  loading={submitMutation.isPending}
                >
                  Submit
                </Button>
              </div>
              {submitMutation.isError && (
                <p className="mt-4 text-red-400 text-sm">
                  Failed to submit. Please try again.
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-wolf-950/80">
          <Card className="max-w-md mx-4 !bg-wolf-900">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-wolf-100 mb-4">Delete Project</h3>
              <p className="text-wolf-300 mb-6">
                Are you sure you want to delete this project? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Re-evaluate Confirmation Dialog */}
      {showReEvaluateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-wolf-950/80">
          <Card className="max-w-md mx-4 !bg-wolf-900">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-wolf-100 mb-4">
                {aiEvaluation ? 'Re-run AI Evaluation' : 'Run AI Evaluation'}
              </h3>
              <p className="text-wolf-300 mb-6">
                {aiEvaluation
                  ? 'This will delete the existing AI evaluation and generate a new one. The project status will be updated to PENDING_EVALUATION. Are you sure you want to continue?'
                  : 'This will trigger the AI to evaluate this project. Are you sure you want to continue?'}
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowReEvaluateDialog(false)}
                  disabled={reEvaluateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => reEvaluateMutation.mutate()}
                  loading={reEvaluateMutation.isPending}
                >
                  Confirm
                </Button>
              </div>
              {reEvaluateMutation.isError && (
                <p className="mt-4 text-red-400 text-sm">
                  Failed to trigger re-evaluation. Please try again.
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Evaluation Section - For reviewers only */}
      {isReviewer && (
        <>
          {/* Human Evaluation Form/Display (AI hidden until submitted) */}
          {framework && (
            <div className="mb-6">
              <EvaluationsSection
                projectId={project.id}
                projectStatus={project.status}
                framework={framework}
                redFlagDefinitions={framework.red_flags ?? []}
                aiEvaluation={aiEvaluation}
                humanEvaluation={humanEvaluation}
                otherReviewerEvaluations={otherReviewerEvaluations}
                isOwnProject={isSubmitter}
              />
            </div>
          )}

          {/* Show AI evaluation standalone only if human hasn't submitted yet and no form showing */}
          {!humanEvaluation && aiEvaluation && !framework && (
            <div className="mb-6">
              <AIEvaluation
                evaluation={aiEvaluation}
                framework={undefined}
                redFlagDefinitions={[]}
              />
            </div>
          )}

          {project.status === 'EVALUATION_FAILED' && !aiEvaluation && (
            <div className="mb-6">
              <Card>
                <div className="flex items-center gap-3 text-wolf-400">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">AI evaluation unavailable for this project</span>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Content */}
      <Card>
        <div className="p-6 space-y-6">
          {/* Description */}
          {project.description && (
            <div>
              <h2 className="text-sm font-medium text-wolf-300 mb-3">Description</h2>
              <p className="text-wolf-100 whitespace-pre-wrap">{project.description}</p>
            </div>
          )}

          {/* Attachment */}
          <div>
            <h2 className="text-sm font-medium text-wolf-300 mb-3">Attachment</h2>
            <AttachmentCard
              project={project}
              canModify={isSubmitter && project.status === 'DRAFT'}
              onUpload={async (file) => { await uploadPdfMutation.mutateAsync(file); }}
              onRemove={async () => { await removePdfMutation.mutateAsync(); }}
              onDownload={async () => { await downloadPdfMutation.mutateAsync(); }}
              isUploading={uploadPdfMutation.isPending}
              isRemoving={removePdfMutation.isPending}
              isDownloading={downloadPdfMutation.isPending}
            />
          </div>

          {/* Empty state if no description and no PDF */}
          {!project.description && !project.pdf_key && (
            <p className="text-wolf-500 italic">No description or document provided.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
